import { useContext, useEffect, useReducer, useState } from "react";

import { getCurrentReinsuranceYear } from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import { AppContext } from "App";
import EmptyCard from "components/Cards/EmptyCard";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import { getUniqueProducerFarmCrops } from "components/Vault/Farm/helpers";
import { useFarmController } from "components/Vault/Farm/useFarmController";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import { useSnackbar } from "notistack";

import { ReportsIcon } from "../../Icons/Icons";
import { fsaSort } from "./helpers";
import { ReportViewer } from "./ReportViewer";

export default function FarmsReport() {
  const { globalState, globalDispatch } = useContext(AppContext);

  const [currentYear, setCurrentYear] = useState(
    globalState?.crop_year ? globalState?.crop_year : getCurrentReinsuranceYear()
  );
  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "crop_year",
        value: currentYear,
      },
    });
  }, [currentYear]);

  const [filter, dispatchFilter] = useReducer(propUpdateReducer, {
    commodity_name: [],
    practice_name: [],
  });

  const { enqueueSnackbar } = useSnackbar();
  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || err, { preventDuplicate: true, variant: "error" });
  };

  const [isDetailViewOnParent, setIsDetailViewOnParent] = useState(false);
  const setIsDetailViewOnFunc = (val) => {
    setIsDetailViewOnParent(val);
  };

  const { counties, producerFarmFieldCrops, isLoadingCounties } = useFarmController({
    crop_year: currentYear,
    filter: filter,
    onError: handleError,
  });

  const columnsDirective = [
    { field: "location_county_name", headerText: "County", width: 100 },
    { field: "location_state_name", headerText: "State", width: 100 },
    { field: "fsa_farm_number", headerText: "Farm Number", width: 100, textAlign: "Right", sortComparer: fsaSort },
    { field: "farm_name", headerText: "Farm Name", width: 100 },
    { field: "unit_count", headerText: "# Fields", width: 100, textAlign: "Right", visible: isDetailViewOnParent },
    { field: "crop_year", headerText: "Year", width: 100, textAlign: "Right", visible: isDetailViewOnParent },
    { field: "reported_acreage", headerText: "Reported Acreage", width: 100, textAlign: "Right", format: "N0" },
    { field: "share_percent", headerText: "Share Percent", width: 100, textAlign: "Right", format: "P0" },
    { field: "commodity_name", headerText: "Commodity", width: 100 },
    { field: "type_name", headerText: "Type", width: 100, visible: isDetailViewOnParent },
    { field: "practice_name", headerText: "Practice", width: 100 },
    { field: "approved_yield_weighted", headerText: "Approved Yield", width: 100, textAlign: "Right" },
    { field: "rate_yield", headerText: "Rate Yield", width: 100, textAlign: "Right" },
    {
      field: "adjusted_yield",
      headerText: "Adjusted Yield",
      width: 100,
      textAlign: "Right",
      format: "N0",
      valueAccessor: (field, data) => {
        return data[field] || "";
      },
    },
    {
      field: "average_yield",
      headerText: "Average Yield",
      width: 100,
      textAlign: "Right",
      format: "N0",
      valueAccessor: (field, data) => {
        return data[field] || "";
      },
    },
  ];

  return (
    <>
      <WorkspaceHeader
        title="Planted Acres by Farm"
        category={{
          title: "Reports",
          icon: <ReportsIcon />,
        }}
      />

      <ReportViewer
        columnsDirective={columnsDirective}
        counties={counties}
        producerFarmFieldCrops={getUniqueProducerFarmCrops(producerFarmFieldCrops)}
        isLoadingCounties={isLoadingCounties}
        reportType="farm"
        setIsDetailViewOnFunc={setIsDetailViewOnFunc}
        currentYear={currentYear}
        setCurrentYear={setCurrentYear}
        gridKey="FarmsReport"
        filter={filter}
        dispatchFilter={dispatchFilter}
      />

      {!isLoadingCounties && !counties?.length && (
        <>
          <EmptyCard text="No search results" secondaryText="Remove some filters or add a new farm in the Farm Vault" />
        </>
      )}
    </>
  );
}
